import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DataGrid, showNotif } from '@p81-common/ui';
import type { DropDownMenuProps } from '@p81-common/ui';
import { getRequest } from 'utils/requests';
import { useDataGrid } from 'hooks/useDataGrid';
import { useTableSettings } from '../helpers/tableSettings';
import {
  EDGE_DETAIL_URL,
  EDGE_NETWORKS_SUFFIX,
  DEFAULT_PAGE_SIZE
} from '../helpers/configurates';
import { createNetworksTableClasses } from './styles';

interface NetworksTableProps {
  edgeId: string;
}

type RoleFilter = 'all' | 'primary' | 'secondary';

const NetworksTable = ({ edgeId }: NetworksTableProps) => {
  const { t } = useTranslation('edges');
  const unmount = useRef(false);
  const [roleFilter, setRoleFilter] = useState<RoleFilter>('all');
  const classes = createNetworksTableClasses();

  const ROLE_LABEL_MAP: Record<RoleFilter, string> = {
    all: t('NETWORKS_TABLE.ROLE_ALL'),
    primary: t('NETWORKS_TABLE.ROLE_PRIMARY'),
    secondary: t('NETWORKS_TABLE.ROLE_SECONDARY')
  };
  const { networksTableConfig } = useTableSettings();

  const {
    filter,
    setFilter,
    setResults,
    setTableData,
    setLoaderTable,
    loaderTable,
    results,
    tableData,
    tableConfigDisplay,
    tableSettingsDisplay,
    numOfRowsOptions,
    changeCurrentPage,
    rowSelectionChanged,
    tableSettingsChanged,
    handleTableSettingsMenuItemClick,
    handleSearchChangeCallback,
    handleChangeNumOfRows,
    onSortEnd
  } = useDataGrid(networksTableConfig, {});

  const onChangeNumOfRows: DropDownMenuProps['handleClick'] = useCallback(
    itemsPerPage => () => {
      (handleChangeNumOfRows(itemsPerPage) as () => void)();
    },
    [handleChangeNumOfRows]
  );

  const fetchNetworks = useCallback(
    async (currentPage: number, role: RoleFilter, itemsPerPage: number) => {
      setLoaderTable(true);
      try {
        const params = new URLSearchParams();
        params.set('page', String(currentPage));
        params.set('page_size', String(itemsPerPage));
        if (role !== 'all') {
          params.set('role', role);
        }

        const url = `${EDGE_DETAIL_URL}/${edgeId}${EDGE_NETWORKS_SUFFIX}?${params.toString()}`;
        const res = await getRequest(url);

        if (!unmount.current) {
          const body = res?.body ?? res;
          setTableData(body?.data || []);
          setResults({
            data: body?.data || [],
            page: body?.pagination?.page || 1,
            totalPages: body?.pagination?.total_pages || 0,
            totalRecords: body?.pagination?.total_items || 0,
            totalGwCount: 0
          });
        }
      } catch (err) {
        if (!unmount.current) {
          setResults({
            data: [],
            page: 1,
            totalPages: 0,
            totalRecords: 0,
            totalGwCount: 0
          });
          showNotif(t('ERRORS.LOAD_NETWORKS_FAILED'), {
            alertProps: { severity: 'error' }
          });
        }
      } finally {
        if (!unmount.current) {
          setLoaderTable(false);
        }
      }
    },
    [edgeId, setLoaderTable, setResults, setTableData, t]
  );

  useEffect(() => {
    (handleChangeNumOfRows(String(DEFAULT_PAGE_SIZE)) as () => void)();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchNetworks(
      filter.currentPage,
      roleFilter,
      filter.itemsPerPage || DEFAULT_PAGE_SIZE
    );
  }, [filter.currentPage, filter.itemsPerPage, roleFilter, fetchNetworks]);

  useEffect(() => {
    return () => {
      unmount.current = true;
    };
  }, []);

  const handleRoleFilter = (role: RoleFilter) => {
    setRoleFilter(role);
    setFilter({ ...filter, currentPage: 1 });
    changeCurrentPage(1);
  };

  return (
    <>
      <div className={classes.roleFilterContainer}>
        {(['all', 'primary', 'secondary'] as RoleFilter[]).map(role => (
          <Button
            key={role}
            variant={roleFilter === role ? 'contained' : 'outlined'}
            color="primary"
            size="small"
            onClick={() => handleRoleFilter(role)}
          >
            {ROLE_LABEL_MAP[role]}
          </Button>
        ))}
      </div>

      <DataGrid
        autoHeight={true}
        tableConfig={tableConfigDisplay}
        tableData={{
          page: results?.page || 0,
          totalPages: results?.totalPages || 0,
          totalRecords: results?.totalRecords || 0,
          data: tableData || []
        }}
        lazyLoading={true}
        loading={loaderTable}
        numOfRowsOptions={numOfRowsOptions}
        rowCount={results?.totalRecords || 0}
        getRowId={(row: any) => row.network_id}
        paginationProps={{
          page: filter.currentPage - 1,
          count: results?.totalRecords || 0,
          rowsPerPage:
            numOfRowsOptions.find(item => item.selected === true)?.name ?? 25,
          onChangePage: (_e, page) => {
            setFilter({ ...filter, currentPage: page + 1 });
            changeCurrentPage(page + 1);
          },
          hideNavigation: !results?.totalRecords,
          hideRowCount: !results?.totalRecords
        }}
        toolbarProps={{
          title1: t('NETWORKS_TABLE.TITLE'),
          title2: `(${results?.totalRecords || 0})`,
          tableSettingsListItems: tableSettingsDisplay,
          rowSelectionChanged,
          tableSettingsChanged,
          handleTableSettingsMenuItemClick,
          handleSearchChangeCallback,
          onChangeNumOfRows,
          onSortEnd
        }}
        sortingMode="client"
        paginationMode="server"
      />
    </>
  );
};

export default NetworksTable;
